import type { DashboardSnapshot, QueuedMutation } from "./domain";

export interface MerchantStorage {
  deleteMutation(id: string): Promise<void>;
  getMutations(): Promise<QueuedMutation[]>;
  getSnapshot(storeId: string): Promise<DashboardSnapshot | null>;
  putMutation(mutation: QueuedMutation): Promise<void>;
  putSnapshot(storeId: string, snapshot: DashboardSnapshot): Promise<void>;
}

const DATABASE_NAME = "gobro-merchant";
const DATABASE_VERSION = 1;

export class IndexedDbMerchantStorage implements MerchantStorage {
  private databasePromise?: Promise<IDBDatabase>;

  async getSnapshot(storeId: string): Promise<DashboardSnapshot | null> {
    return (await this.read<DashboardSnapshot>("snapshots", storeId)) ?? null;
  }

  async putSnapshot(storeId: string, snapshot: DashboardSnapshot): Promise<void> {
    await this.write("snapshots", snapshot, storeId);
  }

  async getMutations(): Promise<QueuedMutation[]> {
    const database = await this.database();
    return new Promise((resolve, reject) => {
      const request = database
        .transaction("mutations", "readonly")
        .objectStore("mutations")
        .getAll();
      request.onsuccess = () =>
        resolve(
          (request.result as QueuedMutation[]).sort((a, b) =>
            a.createdAt.localeCompare(b.createdAt),
          ),
        );
      request.onerror = () => reject(request.error);
    });
  }

  async putMutation(mutation: QueuedMutation): Promise<void> {
    await this.write("mutations", mutation, mutation.id);
  }

  async deleteMutation(id: string): Promise<void> {
    const database = await this.database();
    await new Promise<void>((resolve, reject) => {
      const request = database
        .transaction("mutations", "readwrite")
        .objectStore("mutations")
        .delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async database(): Promise<IDBDatabase> {
    this.databasePromise ??= new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains("snapshots"))
          database.createObjectStore("snapshots");
        if (!database.objectStoreNames.contains("mutations"))
          database.createObjectStore("mutations");
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return this.databasePromise;
  }

  private async read<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
    const database = await this.database();
    return new Promise((resolve, reject) => {
      const request = database.transaction(store, "readonly").objectStore(store).get(key);
      request.onsuccess = () => resolve(request.result as T | undefined);
      request.onerror = () => reject(request.error);
    });
  }

  private async write(store: string, value: unknown, key: IDBValidKey): Promise<void> {
    const database = await this.database();
    await new Promise<void>((resolve, reject) => {
      const request = database.transaction(store, "readwrite").objectStore(store).put(value, key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export class MemoryMerchantStorage implements MerchantStorage {
  readonly mutations = new Map<string, QueuedMutation>();
  readonly snapshots = new Map<string, DashboardSnapshot>();

  async deleteMutation(id: string) {
    this.mutations.delete(id);
  }
  async getMutations() {
    return [...this.mutations.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }
  async getSnapshot(storeId: string) {
    return this.snapshots.get(storeId) ?? null;
  }
  async putMutation(mutation: QueuedMutation) {
    this.mutations.set(mutation.id, mutation);
  }
  async putSnapshot(storeId: string, snapshot: DashboardSnapshot) {
    this.snapshots.set(storeId, snapshot);
  }
}
